import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  Card, 
  Spin, 
  message, 
  Button, 
  Modal, 
  Tooltip, 
  Space, 
  Form, 
  Input, 
  Tag, 
  Typography,
  Select,
  notification,
  Row,
  Col,
  Drawer
} from 'antd';
import { Document, Page, PDFViewer } from '@react-pdf/renderer';
import { 
  FilePdfOutlined, 
  ReloadOutlined, 
  FullscreenOutlined, 
  FullscreenExitOutlined, 
  EyeOutlined, 
  EyeInvisibleOutlined,
  MailOutlined,
  PlusOutlined,
  DeleteOutlined,
  SendOutlined,
  CloseOutlined
} from '@ant-design/icons';


export function replacePlaceholders(config, dataMap) {
  const updatedConfig = JSON.parse(JSON.stringify(config));

  function traverseAndPopulate(obj) {
    for (const key in obj) {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        if (obj[key].type === 'collection' && Array.isArray(dataMap[key])) {
          obj[key].content.items = dataMap[key];
        }
        else if (obj[key].type === 'groupedCollection' && Array.isArray(dataMap[key])) {
          obj[key].content.items = dataMap[key];
        } else if (obj[key].type === 'bloc' && dataMap[key] && dataMap[key].content) {
          obj[key].content = dataMap[key].content;
        } else {
          traverseAndPopulate(obj[key]);
        }
      }
    }
  }

  traverseAndPopulate(updatedConfig);

  return updatedConfig;
}
const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const Printer = ({ 
  configEndpoint, 
  generateEndpoint, 
  data,
  patient,
  workOrder,
  recipientList = [],
  emailTemplates = [],
  /** When true, no outer Card; tight layout for use inside another Modal */
  embed = false,
  /** When true, fetch and generate PDF as soon as `data` and auth are ready */
  autoGenerate = false,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  
  const [loading, setLoading] = useState(() => Boolean(autoGenerate));
  const [pdfUrl, setPdfUrl] = useState(null);
  const [error, setError] = useState(null);
  const [useIframe, setUseIframe] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [emailDrawerVisible, setEmailDrawerVisible] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const recipientsInitialized = useRef(false);

  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  // Reset recipients when drawer closes
  useEffect(() => {
    if (!emailDrawerVisible) {
      recipientsInitialized.current = false;
      return;
    }
  }, [emailDrawerVisible]);

  // Initialize recipients only once when drawer opens
  useEffect(() => {
    if (!emailDrawerVisible || recipientsInitialized.current) return;
    
    if (recipientList && recipientList.length > 0) {
      setRecipients(recipientList);
      recipientsInitialized.current = true;
      return;
    }
    
    const emails = [];
    
    if (patient?.email) {
      emails.push({
        email: patient.email,
        name: `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
        type: 'patient'
      });
    }

    if (patient?.emergencyContactEmail) {
      emails.push({
        email: patient.emergencyContactEmail,
        name: patient.emergencyContactName || 'Emergency Contact',
        type: 'emergency'
      });
    }

    if (patient?.practitioners && Array.isArray(patient.practitioners)) {
      patient.practitioners.forEach(practitioner => {
        if (practitioner.email) {
          emails.push({
            email: practitioner.email,
            name: `${practitioner.forename || ''} ${practitioner.surname || ''}`.trim(),
            type: 'practitioner'
          });
        }
      });
    }

    if (patient?.organisations && Array.isArray(patient.organisations)) {
      patient.organisations.forEach(organisation => {
        if (organisation.email) {
          emails.push({
            email: organisation.email,
            name: organisation.name || 'Organisation',
            type: 'organisation'
          });
        }
      });
    }

    if (workOrder) {
      if (workOrder.orderingPractitionerEmail) {
        emails.push({
          email: workOrder.orderingPractitionerEmail,
          name: workOrder.orderingPractitioner || 'Ordering Practitioner',
          type: 'ordering_practitioner'
        });
      }

      if (workOrder.invoiceReport?.customerEmail) {
        emails.push({
          email: workOrder.invoiceReport.customerEmail,
          name: workOrder.invoiceReport.customerName || 'Customer',
          type: 'customer'
        });
      }

      if (workOrder.testResultReport?.resultByEmail) {
        emails.push({
          email: workOrder.testResultReport.resultByEmail,
          name: workOrder.testResultReport.resultBy || 'Result By',
          type: 'result_by'
        });
      }

      if (workOrder.testResultReport?.verifiedByEmail) {
        emails.push({
          email: workOrder.testResultReport.verifiedByEmail,
          name: workOrder.testResultReport.verifiedBy || 'Verified By',
          type: 'verified_by'
        });
      }
    }

    // If no recipients found, add one empty recipient field by default
    if (emails.length === 0) {
      emails.push({
        email: '',
        name: '',
        type: 'custom'
      });
    }

    setRecipients(emails);
    recipientsInitialized.current = true;
  }, [emailDrawerVisible, recipientList, patient, workOrder]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const generatePDF = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: configData } = await axios.get(configEndpoint, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`
        }
      });

      const request = replacePlaceholders(configData.content, data);

      const pdfResponse = await axios.post(generateEndpoint, request, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`
        },
        responseType: 'blob',
      });

      const contentType = pdfResponse.headers['content-type'];
      if (contentType !== 'application/pdf') {
        throw new Error(t('Received non-PDF response: {{contentType}}', { contentType }));
      }

      const pdfBlob = new Blob([pdfResponse.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);
    } catch (error) {
      console.error(t('Error fetching config or generating PDF:'), error);
      setError(error.message || t('Failed to generate PDF'));
      message.error(t('Failed to generate PDF'));
    } finally {
      setLoading(false);
    }
  }, [configEndpoint, generateEndpoint, data, userInfo?.token, t]);

  useEffect(() => {
    if (!autoGenerate) return;
    if (!data || !userInfo?.token) {
      setLoading(false);
      return;
    }
    generatePDF();
  }, [autoGenerate, data, userInfo?.token, generatePDF]);

  const toggleViewerType = () => {
    setUseIframe(!useIframe);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const addRecipient = () => {
    const newRecipient = {
      email: '',
      name: '',
      type: 'custom'
    };
    setRecipients([...recipients, newRecipient]);
  };

  const removeRecipient = (index) => {
    // Prevent removing the last recipient
    if (recipients.length <= 1) {
      message.warning(t('At least one recipient is required'));
      return;
    }
    const newRecipients = recipients.filter((_, i) => i !== index);
    setRecipients(newRecipients);
  };

  const updateRecipient = (index, field, value) => {
    const newRecipients = [...recipients];
    newRecipients[index] = { ...newRecipients[index], [field]: value };
    setRecipients(newRecipients);
  };

  const handleTemplateSelect = (templateId) => {
    const template = emailTemplates.find(t => t.id === templateId);
    if (template) {
      setSelectedTemplate(template);
      form.setFieldsValue({
        subject: template.subject,
        message: template.body
      });
    }
  };

  const openEmailDrawer = () => {
    if (!pdfUrl) {
      message.error(t('Please generate PDF first'));
      return;
    }
    setEmailDrawerVisible(true);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const sendEmail = async () => {
    try {
      const values = await form.validateFields();
      setSendingEmail(true);

      // Validate recipient emails
      const invalidRecipients = recipients.filter(r => r.email && r.email.trim() && !validateEmail(r.email.trim()));
      if (invalidRecipients.length > 0) {
        message.error(t('Please enter valid email addresses'));
        setSendingEmail(false);
        return;
      }

      const validRecipients = recipients.filter(r => r.email && r.email.trim() && validateEmail(r.email.trim()));

      if (validRecipients.length === 0) {
        message.error(t('Please add at least one valid recipient email'));
        setSendingEmail(false);
        return;
      }

      if (!pdfUrl) {
        message.error(t('PDF must be generated before sending email'));
        return;
      }

      const pdfBlob = await fetch(pdfUrl).then(res => res.blob());
      
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (pdfBlob.size > maxSize) {
        message.error(t('PDF file is too large (max 10MB)'));
        return;
      }
      
      const pdfBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const result = reader.result;
            const base64 = result.split(',')[1];
            resolve(base64);
          } catch (error) {
            reject(new Error('Failed to convert PDF to base64'));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read PDF file'));
        reader.readAsDataURL(pdfBlob);
      });

      const emailPromises = validRecipients.map(recipient => {
        const emailData = {
          EmailToId: recipient.email,
          EmailToName: recipient.name || recipient.email,
          EmailSubject: values.subject,
          EmailBody: values.message,
          Attachments: [
            {
              FileName: `report_${workOrder?.orderNumber || Date.now()}.pdf`,
              ContentType: 'application/pdf',
              Content: pdfBase64
            }
          ]
        };

        return axios.post('/dotnet_api/v1/auth/sendmail', emailData, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userInfo.token}`
          }
        });
      });

      await Promise.all(emailPromises);
      
      message.success(t('Emails sent successfully to {{count}} recipients', { count: validRecipients.length }));
      api.success({
        message: t('Success'),
        description: t('Emails sent successfully to {{count}} recipients', { count: validRecipients.length }),
        placement: 'bottom',
      });
      
      setEmailDrawerVisible(false);
    } catch (error) {
      console.error('Error sending emails:', error);
      message.error(t('Failed to send emails: {{error}}', { error: error.message }));
      api.error({
        message: t('Error'),
        description: t('Failed to send emails: {{error}}', { error: error.message }),
        placement: 'bottom',
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const getRecipientTypeColor = (type) => {
    switch (type) {
      case 'patient': return 'blue';
      case 'emergency': return 'red';
      case 'practitioner': return 'green';
      case 'organisation': return 'purple';
      case 'ordering_practitioner': return 'cyan';
      case 'customer': return 'magenta';
      case 'result_by': return 'lime';
      case 'verified_by': return 'geekblue';
      case 'custom': return 'orange';
      default: return 'default';
    }
  };

  const renderPDFViewer = () => {
    const viewerStyle = isFullscreen
      ? { width: '100%', height: 'calc(100vh - 64px)' }
      : {
          width: '100%',
          height: embed ? 'min(70vh, 720px)' : '70vh',
          border: 'none',
          display: 'block',
        };

    return useIframe ? (
      <iframe
        src={pdfUrl}
        style={viewerStyle}
        title={t('PDF Viewer')}
      />
    ) : (
      <PDFViewer style={viewerStyle}>
        <Document file={pdfUrl}>
          <Page pageNumber={1} />
        </Document>
      </PDFViewer>
    );
  };

  const toolbar = (
    <Space size="small">
      <Tooltip title={autoGenerate ? t('Regenerate PDF') : t('Generate PDF')}>
        <Button
          type="text"
          icon={<ReloadOutlined />}
          onClick={generatePDF}
          disabled={loading}
          size="small"
        />
      </Tooltip>
      {pdfUrl && (
        <>
          <Tooltip title={t('Send Email')}>
            <Button
              type="text"
              icon={<MailOutlined />}
              onClick={openEmailDrawer}
              disabled={!pdfUrl}
              size="small"
            />
          </Tooltip>
          <Tooltip title={useIframe ? t('Switch to PDFViewer') : t('Switch to Browser PDF Viewer')}>
            <Button
              type="text"
              icon={useIframe ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              onClick={toggleViewerType}
              size="small"
            />
          </Tooltip>
          <Tooltip title={isFullscreen ? t('Exit Fullscreen') : t('Fullscreen')}>
            <Button
              type="text"
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
              size="small"
            />
          </Tooltip>
        </>
      )}
    </Space>
  );

  const contentMinHeight = embed ? { minHeight: 280, height: 'min(70vh, 720px)' } : { height: '70vh' };

  const mainContent = loading ? (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: embed ? 0 : undefined,
      ...contentMinHeight,
    }}
    >
      <Spin size="large" tip={t('Generating PDF...')} />
    </div>
  ) : error ? (
    <div style={{ padding: embed ? '8px 0' : '20px', textAlign: 'center' }}>
      <p style={{ color: 'red' }}>{t('Error')}: {error}</p>
      <Button onClick={generatePDF} size="small">{t('Try Again')}</Button>
    </div>
  ) : pdfUrl ? (
    renderPDFViewer()
  ) : autoGenerate ? (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 0,
      ...contentMinHeight,
    }}
    >
      <Spin tip={t('Generating PDF...')} />
    </div>
  ) : (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      ...contentMinHeight,
    }}
    >
      <Button icon={<FilePdfOutlined />} onClick={generatePDF} size="large">
        {t('Generate PDF')}
      </Button>
    </div>
  );

  return (
    <>
      {contextHolder}
      {embed ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          padding: 0,
          margin: 0,
          minHeight: 0,
        }}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>{toolbar}</div>
          <div style={{ flex: 1, minHeight: 0 }}>{mainContent}</div>
        </div>
      ) : (
      <Card
        title={<Space><FilePdfOutlined /> {t('PDF Printer')}</Space>}
        extra={toolbar}
      >
      {mainContent}

      </Card>
      )}

      <Modal
        open={isFullscreen}
        onCancel={toggleFullscreen}
        footer={null}
        width="100%"
        style={{ top: 0, paddingBottom: 0 }}
        styles={{ body: { height: '100vh', padding: 0 } }}
      >
        {pdfUrl && renderPDFViewer()}
      </Modal>

      <Drawer
        title={
          <Space>
            <MailOutlined />
            <Title level={4} style={{ margin: 0 }}>
              {t('Send Email with PDF Attachment')}
            </Title>
          </Space>
        }
        placement="right"
        width={600}
        visible={emailDrawerVisible}
        onClose={() => setEmailDrawerVisible(false)}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setEmailDrawerVisible(false)} icon={<CloseOutlined />}>
              {t('Cancel')}
            </Button>
            <Button
              type="primary"
              loading={sendingEmail}
              icon={<SendOutlined />}
              onClick={sendEmail}
              disabled={!pdfUrl}
            >
              {t('Send Email')}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          {/* Recipients Section */}
          <div style={{ marginBottom: '16px' }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text strong>{t('Recipients')}</Text>
                <Button 
                  type="dashed" 
                  icon={<PlusOutlined />} 
                  onClick={addRecipient}
                  size="small"
                >
                  {t('Add Recipient')}
                </Button>
              </div>
              
              {recipients.map((recipient, index) => {
                const isEmailValid = !recipient.email || validateEmail(recipient.email.trim());
                const isEmailRequired = recipients.length === 1 || recipient.email.trim();
                
                return (
                  <div key={index} style={{ 
                    display: 'flex', 
                    gap: '8px', 
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    width: '100%'
                  }}>
                    <Tag color={getRecipientTypeColor(recipient.type)}>
                      {t(recipient.type)}
                    </Tag>
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <Input
                        placeholder={t('Email') + (index === 0 ? ' *' : '')}
                        value={recipient.email}
                        onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                        status={recipient.email && !isEmailValid ? 'error' : ''}
                        required={index === 0}
                      />
                      {recipient.email && !isEmailValid && (
                        <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                          {t('Please enter a valid email address')}
                        </div>
                      )}
                      {index === 0 && !recipient.email && (
                        <div style={{ color: '#ff4d4f', fontSize: '12px', marginTop: '4px' }}>
                          {t('Email is required')}
                        </div>
                      )}
                    </div>
                    <Input
                      placeholder={t('Name (optional)')}
                      value={recipient.name}
                      onChange={(e) => updateRecipient(index, 'name', e.target.value)}
                      style={{ width: '120px' }}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeRecipient(index)}
                      size="small"
                      disabled={recipients.length === 1}
                      title={recipients.length === 1 ? t('At least one recipient is required') : t('Remove recipient')}
                    />
                  </div>
                );
              })}
            </Space>
          </div>

          {/* Email Templates */}
          {emailTemplates.length > 0 && (
            <Form.Item label={t('Email Templates')}>
              <Select
                placeholder={t('Select a template')}
                onChange={handleTemplateSelect}
                style={{ width: '100%' }}
              >
                {emailTemplates.map(template => (
                  <Option key={template.id} value={template.id}>
                    {template.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item 
            name="subject" 
            label={t('Subject')} 
            rules={[{ required: true, message: t('Please input the email subject!') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item 
            name="message" 
            label={t('Message')} 
            rules={[{ required: true, message: t('Please input the email message!') }]}
          >
            <TextArea rows={6} />
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default Printer;
